/**
 * NgwEditorState — `IEditorState` 의 NgwEditor 구현 (PR-N1).
 *
 * NgwEditor 의 표면 노출:
 *   - undo / redo / 변경 추적: 어댑터 내부 dirty 플래그 (lifecycle.onChange 가 markDirty 호출)
 *   - 링크: `EditorHandle.executeCommand('setLink', { url, text })` / `'removeLink'` (D-N4 매핑)
 *   - 무입력 타이머: 어댑터 멤버 setTimeout (cleanup 보장)
 *
 * **resetUndoHistory 우회 (D-N4)**: NgwEditor 0.2.10 이 history reset 직접 API 를 노출하지
 * 않는다. 본 구현은 현재 본문을 setHTML 으로 재호출하여 PM EditorState 를 새로 생성 →
 * history plugin 의 done/undone 스택이 자연 초기화. 메서드는 동기 반환.
 *
 * **PR-N1 stub vs 본 구현**: `getLink` / `setLink` / `requestLinkInfo` 는 NgwEditor 의 links
 * feature 직접 위임이라 PR-N3 (Command 매핑) 진입 직전까지 정밀화 보류. 본 PR 단계에서는
 * executeCommand 표면을 직접 호출하는 형태로 구현 — PR-N6 의 selection 변화 감지 wiring 도
 * 함께 도착하면 requestLinkInfo 가 정상 작동.
 */

import { EditorState, type Mark } from '@ngweditor/react'
import type { IEditorState } from '../IEditorState'
import type { EditorLinkInfo } from '../types'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

export class NgwEditorState implements IEditorState {
  private readonly adapter: NgwEditorAdapter
  private noInputTimerId: number | null = null
  private noInputTimeMs: number = 0

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  resetChangedState(): void {
    this.adapter.clearDirty()
    // mail region 의 snapshot 도 함께 갱신 (2026-05-19 보강).
    //
    // 배경: NgwEditorMailRegion.isChanged() 는 `lastSnapshot == null` 이면 false 를 반환한다.
    // EmailForm.isModified() 가 본문/quoted 변경 감지를 위해 `mail.region.nBody.isChanged()` /
    // `quotedBody.isChanged()` 를 호출하므로, 이 lastSnapshot 이 영구 null 이면 isModified 가
    // 항상 false → notifyNoInput 후 callSave 가 silent skip (자동 저장 미발동) 회귀.
    // resetChangedState 는 메일 load / save 완료 시점에 호출되어 baseline 을 잡는 위치이므로
    // 본 시점에 nBody / signature / quotedHeader / quotedBody 의 snapshot 도 함께 reset.
    const mail = this.adapter.mail
    mail.region.nBody.resetSnapshot()
    mail.region.blankSign.resetSnapshot()
    mail.region.signature.resetSnapshot()
    mail.region.blankMsg.resetSnapshot()
    mail.region.quotedHeader.resetSnapshot()
    mail.region.quotedBody.resetSnapshot()
  }

  resetUndoHistory(_resetTypingFlag?: boolean): void {
    // D-1 (ngwEditor-migration-0.2.12): doc 재구성 없이 PM EditorState 만 새로 생성하여
    // history plugin 의 done/undone 스택 재초기화. QA Q3-2 권장 패턴.
    //
    // 기존 패턴 (getHTML → trailingBreak strip → setHTML 라운드트립) 의 문제:
    //   - doc 재구성 비용 (parseDOM + schema validation 전체 통과)
    //   - 빈 paragraph 보강/제거가 외부 코드 개입 시 누적 회귀 가능 (2026-05-19 fix)
    //   - QA Q3-2 가 명시한 "doc 재구성 없이 EditorState.create" 권장과 어긋남
    //
    // 신 패턴: EditorState.create({doc, plugins, selection}) — doc 인스턴스 그대로 재사용 →
    // 정규화 후처리 미경유 → 빈 paragraph 누적 원천 차단. plugins 배열 재사용으로
    // history plugin 도 새 state 와 함께 자연 초기화.
    //
    // 주의: view.updateState 는 PM 표준 dispatchTransaction hook 을 거치지 않으므로 NgwEditor
    // 의 onChange 콜백이 발화하지 않는다. history reset 으로는 dirty/onChange 가 트리거되지
    // 말아야 하므로 의도된 동작 (design.md §3 / §6.4 분석).
    const core = this.adapter.lifecycle.getCore()
    const view = core?.view
    if (view == null) {
      return
    }
    const newState = EditorState.create({
      doc: view.state.doc,
      plugins: view.state.plugins,
      selection: view.state.selection,
    })
    view.updateState(newState)
  }

  getLink(): EditorLinkInfo | null {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return null
    }
    // NgwEditor 의 links feature: `getCurrentLink` 명령 — feature 직접 위임.
    const raw = handle.getFeature('links') as
      | { getCurrentLink?: () => { url?: string; text?: string } | null }
      | undefined
    const info = raw?.getCurrentLink?.()
    if (info == null || info.url == null) {
      return null
    }
    return {
      url: info.url,
      text: info.text ?? info.url,
      isHTML: false,
    }
  }

  setLink(url: string, text: string, _isHTML: boolean): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) {
      return
    }
    // pr-n0-inventory §3 매핑: `setLink` 명령 + (url, text) 인자.
    handle.executeCommand('setLink', { url, text })
  }

  requestLinkInfo(): void {
    // PR-N6 본 구현 — 현재 selection 의 link mark 를 조회해 handler.showCreateLinkView 발화.
    // PR-7a-1 raw 시그니처 보존: (url, text, type).
    //
    // 링크 mark 가 존재하면 mark.attrs.href + selection 텍스트로 응답. 미존재 시 ('', '', 'text')
    // 로 발화 — parent (EmailForm) 는 빈 url 을 "신규 링크 입력" 모달 분기로 사용.
    const handler = this.adapter.getHandler()
    if (handler?.showCreateLinkView == null) {
      return
    }
    const info = this.collectLinkInfo()
    handler.showCreateLinkView(info.url, info.text, info.type)
  }

  /**
   * 현재 selection 의 link mark 조회. mark.attrs.href 가 있으면 (href, selectionText, 'text') 반환.
   * 없으면 ('', selectionText, 'text') — parent 가 "신규 링크 입력" 모달 분기.
   */
  private collectLinkInfo(): { url: string; text: string; type: string } {
    const view = this.adapter.lifecycle.getBridgeView()
    if (view == null) {
      return { url: '', text: '', type: 'text' }
    }
    const { state } = view
    const { from, to, empty } = state.selection
    const selectionText = empty ? '' : state.doc.textBetween(from, to, ' ', ' ')

    const markType = state.schema.marks.link
    if (markType == null) {
      return { url: '', text: selectionText, type: 'text' }
    }
    const marksAtFrom = state.doc.resolve(from).marks()
    const linkMark = marksAtFrom.find((m: Mark) => m.type === markType)
    if (linkMark == null) {
      return { url: '', text: selectionText, type: 'text' }
    }
    const hrefRaw = linkMark.attrs.href
    const href = typeof hrefRaw === 'string' ? hrefRaw : ''
    return { url: href, text: selectionText, type: 'text' }
  }

  setNoInputTime(milliseconds: number): void {
    // 레거시 editorMgr.setNoInputTime 과 동등 — 값만 저장. 타이머는 checkNoInput 호출 시 시작.
    this.noInputTimeMs = milliseconds
  }

  clearNoInputTimer(): void {
    if (this.noInputTimerId == null) {
      return
    }
    const win = this.getSafeWindow()
    if (win != null) {
      win.clearTimeout(this.noInputTimerId)
    }
    this.noInputTimerId = null
  }

  checkNoInput(): void {
    // 레거시 editorMgr.checkNoInput 과 동등 — 기존 타이머 해제 후 noInputTimeMs 만큼
    // 디바운싱하여 notifyNoInput 발화. 즉시 발화는 타이핑 중 callSave 가 form 을
    // 리렌더시켜 input 포커스를 빼앗기 때문에 금지.
    this.clearNoInputTimer()
    if (this.noInputTimeMs <= 0) {
      return
    }
    const win = this.getSafeWindow()
    if (win == null) {
      return
    }
    this.noInputTimerId = win.setTimeout(() => {
      this.noInputTimerId = null
      this.adapter.getHandler()?.notifyNoInput?.()
    }, this.noInputTimeMs)
  }

  clearObservers(): void {
    // dispose 시점에서 흡수되므로 본 PR 단계에서는 명시 동작 없음.
    // PR-N4 (mail region) / PR-N6 (event wiring) 에서 MutationObserver 가 추가되면 여기에 정리.
    this.clearNoInputTimer()
  }

  /** dispose 시점에 호출되는 정리 path 도 동일하므로 분리. */
  private getSafeWindow(): Window | null {
    try {
      return this.adapter.host.getWindow()
    } catch {
      return null
    }
  }
}
